import React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import TerminalIcon from '@mui/icons-material/Terminal';
import DescriptionIcon from '@mui/icons-material/Description';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LinkIcon from '@mui/icons-material/Link';

import { download } from '../common/download';
import {
    AiDesignerContext, IActivity, IConversation, IImageInput, IModelChoice, IRevision, IRoom, IShot, TFeedItem, TScanResult,
} from './AiDesignerContext';
import { Markdown } from './Markdown';

/**
 * "AI tervező" tab of the AI designer page (/ai-designer): images or PDFs and a brief in, the AI
 * designer's work out - its notes as it goes, every script it runs with the output, the Designer's
 * screenshots, and the ucon + StyleScript it verified last, which stay loaded in the Designer.
 * Every conversation designs for a room: the room bar steps through its revisions (the AR app's
 * too), and the earlier conversations - the shop's, from the page or the app - open again.
 * See docs/admin/AI_DESIGNER.md.
 */

const MAX_IMAGES = 6;
const MAX_EDGE = 2048;
/** the service's limit for one attachment */
const MAX_PDF_BYTES = 10 * 1024 * 1024;

/** Re-encodes an image as a JPEG of at most MAX_EDGE px on its long side, on white (for transparent drawings). */
async function prepareImage(file: File): Promise<IImageInput> {
    const url = URL.createObjectURL(file);
    try {
        const image = await new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error(`Nem olvasható kép: ${file.name}`));
            img.src = url;
        });
        const scale = Math.min(1, MAX_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext('2d')!;
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const preview = canvas.toDataURL('image/jpeg', 0.9);
        return { name: file.name, mediaType: 'image/jpeg', data: preview.split(',')[1], preview };
    } finally {
        URL.revokeObjectURL(url);
    }
}

const isPdf = (file: File) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

/** A PDF goes as it is; its thumbnail is a tile with its name. */
async function preparePdf(file: File): Promise<IImageInput> {
    if (file.size > MAX_PDF_BYTES) {
        throw new Error(`Túl nagy PDF: ${file.name} (legfeljebb ${MAX_PDF_BYTES / 1048576} MB)`);
    }
    const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error(`Nem olvasható PDF: ${file.name}`));
        reader.readAsDataURL(file);
    });
    const name = file.name.length > 14 ? `${file.name.slice(0, 12)}…` : file.name;
    const escaped = name.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
    const tile = '<svg xmlns="http://www.w3.org/2000/svg" width="72" height="72" viewBox="0 0 72 72">'
        + '<rect width="72" height="72" fill="#f5f5f5"/>'
        + '<text x="36" y="36" text-anchor="middle" font-family="sans-serif" font-size="18" font-weight="bold" fill="#c62828">PDF</text>'
        + `<text x="36" y="56" text-anchor="middle" font-family="sans-serif" font-size="8" fill="#555">${escaped}</text></svg>`;
    return { name: file.name, mediaType: 'application/pdf', data: dataUrl.split(',')[1],
        preview: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(tile)}` };
}

/** A PDF's thumbnail is its tile, not a picture to open large. */
const isPdfTile = (preview: string) => preview.startsWith('data:image/svg+xml');

const ACTIVITY_TEXT: Record<IActivity['state'], string> = {
    idle: '',
    thinking: 'Gondolkodik…',
    writing: 'Ír…',
    tool: 'Futtat…',
    designer: 'A Designerben dolgozik…',
};

function activityText(activity: IActivity): string {
    if (activity.state === 'designer') {
        if (activity.detail === 'apply') return 'Betölti a tervet a Designerbe…';
        if (activity.detail === 'screenshot') return 'Képernyőképet készít…';
        return `A Designerből olvas (${activity.detail})…`;
    }
    if (activity.state === 'writing' && activity.detail === 'write_file') return 'Fájlt ír…';
    return ACTIVITY_TEXT[activity.state];
}

const EFFORT_TEXT: Record<string, string> = {
    low: 'alacsony', medium: 'közepes', high: 'magas', xhigh: 'nagyon magas', max: 'maximális',
};

/** Claude Opus 5.5 · $4 / $20 per million tokens (in / out) */
const modelText = (m: IModelChoice) => (m.price ? `${m.label} · $${m.price[0]} / $${m.price[1]}` : m.label);

const monoBox = {
    fontFamily: 'monospace', fontSize: 11, lineHeight: 1.45, whiteSpace: 'pre-wrap' as const, wordBreak: 'break-word' as const,
    bgcolor: '#f5f5f5', color: '#1a1a1a', border: '1px solid #e0e0e0', borderRadius: 1, p: 1, m: 0,
    maxHeight: 320, overflow: 'auto',
};

/** Images and PDFs picked, dropped or pasted, with thumbnails; used by the brief and by follow-ups. */
function useImagePicker() {
    const [images, setImages] = React.useState<IImageInput[]>([]);
    const [error, setError] = React.useState<string | null>(null);
    const input = React.useRef<HTMLInputElement>(null);

    const add = React.useCallback(async (files: FileList | File[]) => {
        const list = Array.from(files).filter((f) => f.type.startsWith('image/') || isPdf(f));
        if (!list.length) return;
        setError(null);
        try {
            const prepared = await Promise.all(list.map((f) => (isPdf(f) ? preparePdf(f) : prepareImage(f))));
            setImages((current) => [...current, ...prepared].slice(0, MAX_IMAGES));
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    }, []);

    const picker = (
        <input ref={input} type="file" accept="image/*,application/pdf,.pdf" multiple hidden
            onChange={(e) => { if (e.target.files) add(e.target.files); e.target.value = ''; }} />
    );
    return {
        images, error, add, picker,
        open: () => input.current?.click(),
        remove: (i: number) => setImages((current) => current.filter((_, j) => j !== i)),
        clear: () => setImages([]),
        onPaste: (e: React.ClipboardEvent) => { if (e.clipboardData.files.length) add(e.clipboardData.files); },
    };
}

const Thumbs: React.FC<{ images: string[]; onRemove?: (i: number) => void; onOpen?: (src: string) => void }> =
    ({ images, onRemove, onOpen }) => (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {images.map((src, i) => {
                const open = onOpen && !isPdfTile(src) ? onOpen : undefined;
                return <Box key={i} sx={{ position: 'relative' }}>
                    <Box component="img" src={src} alt="" onClick={() => open?.(src)}
                        sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd',
                            cursor: open ? 'zoom-in' : 'default', display: 'block' }} />
                    {onRemove && (
                        <IconButton size="small" aria-label="melléklet eltávolítása" onClick={() => onRemove(i)}
                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', border: '1px solid #ddd',
                                p: '2px', '&:hover': { bgcolor: '#f0f0f0' } }}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    )}
                </Box>;
            })}
        </Stack>
    );

const TOOL_ICON: Record<string, React.ReactNode> = {
    run: <TerminalIcon sx={{ fontSize: 16 }} />,
    read_file: <DescriptionIcon sx={{ fontSize: 16 }} />,
    write_file: <EditNoteIcon sx={{ fontSize: 16 }} />,
    edit_file: <EditNoteIcon sx={{ fontSize: 16 }} />,
    list_files: <FolderOpenIcon sx={{ fontSize: 16 }} />,
};

const ToolRow: React.FC<{ item: Extract<TFeedItem, { kind: 'tool' }>; onOpenImage: (src: string) => void }> =
    ({ item, onOpenImage }) => {
        const [open, setOpen] = React.useState(false);
        const shots = item.images.map((shot: IShot) => `data:image/png;base64,${shot.data}`);
        return (
            <Box>
                <Stack direction="row" spacing={0.75} alignItems="center"
                    onClick={() => item.output && setOpen(!open)}
                    sx={{ cursor: item.output ? 'pointer' : 'default', color: 'text.secondary' }}>
                    {item.state === 'running'
                        ? <CircularProgress size={14} />
                        : item.state === 'ok' && (item.exit === undefined || item.exit === 0)
                            ? <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            : <ErrorIcon sx={{ fontSize: 16, color: item.state === 'error' ? 'error.main' : 'warning.main' }} />}
                    {TOOL_ICON[item.name] ?? <TerminalIcon sx={{ fontSize: 16 }} />}
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>
                        {item.input}
                    </Typography>
                </Stack>
                <Collapse in={open} unmountOnExit>
                    <Box component="pre" sx={{ ...monoBox, mt: 0.5 }}>{item.output}</Box>
                </Collapse>
                {shots.length > 0 && (
                    <Box sx={{ mt: 0.75, pl: 3 }}>
                        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                            {shots.map((src, i) => (
                                <Box key={i} component="img" src={src} alt={item.images[i].path} title={item.images[i].path}
                                    onClick={() => onOpenImage(src)}
                                    sx={{ width: 'calc(50% - 4px)', borderRadius: 1, border: '1px solid #ddd', cursor: 'zoom-in' }} />
                            ))}
                        </Stack>
                    </Box>
                )}
            </Box>
        );
    };

const ThinkingRow: React.FC<{ text: string }> = ({ text }) => {
    const [open, setOpen] = React.useState(false);
    return (
        <Box onClick={() => setOpen(!open)} sx={{ cursor: 'pointer' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block',
                ...(open ? { whiteSpace: 'pre-wrap' } : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) }}>
                Gondolkodás: {text}
            </Typography>
        </Box>
    );
};

/**
 * A room scan (.usdz from an iPhone or iPad) made into a room in the Designer, before a brief: the
 * admin checks it there, and "A látható tervből indulj ki" (ticked by a scan that worked) designs in it.
 */
const ScanRoom: React.FC<{ onLoaded: () => void }> = ({ onLoaded }) => {
    const ai = React.useContext(AiDesignerContext);
    const input = React.useRef<HTMLInputElement>(null);
    const [busy, setBusy] = React.useState(false);
    const [result, setResult] = React.useState<(TScanResult & { name: string }) | null>(null);

    const pick = async (file: File) => {
        setBusy(true);
        setResult(null);
        const answer = await ai.loadScan(file);
        setBusy(false);
        setResult({ ...answer, name: file.name });
        if (answer.ok) onLoaded();
    };

    return (
        <Box>
            <Button size="small" variant="outlined" startIcon={busy ? <CircularProgress size={14} /> : <ViewInArIcon />}
                disabled={busy} onClick={() => input.current?.click()}>
                Szoba szkennelésből (.usdz)
            </Button>
            <input ref={input} type="file" accept=".usdz,model/vnd.usdz+zip" hidden
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) pick(f); }} />
            {result?.ok && (
                <Alert severity="success" sx={{ mt: 1 }}>
                    A szoba betöltve a Designerbe ({result.name}): ellenőrizze a falakat, ajtókat és ablakokat, mielőtt
                    elindítja a tervezést. Alább a szkennelés leírása; a felsorolt bútorok nincsenek megépítve.
                    <Box component="pre" sx={{ ...monoBox, mt: 1 }}>{result.report.join('\n')}</Box>
                </Alert>
            )}
            {result && !result.ok && <Alert severity="warning" sx={{ mt: 1 }}>{result.error}</Alert>}
        </Box>
    );
};

const REVISION_SOURCE: Record<IRevision['source'], string> = {
    'page-agent': 'AI', 'app-agent': 'AI, telefonon', 'page-manual': 'kézi', 'app-manual': 'kézi, telefonon',
};

const when = (iso: string) => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('hu-HU', { dateStyle: 'short', timeStyle: 'short' });
};

/** This page's address with one parameter: the link to a room or a conversation. */
const linkTo = (key: 'room' | 'conversation', id: string) =>
    `${window.location.origin}${window.location.pathname}?${key}=${encodeURIComponent(id)}`;

/** A button that copies a link (and says so for a moment). */
const CopyLink: React.FC<{ label: string; url: string }> = ({ label, url }) => {
    const [copied, setCopied] = React.useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
        } catch {
            window.prompt('A link:', url);      // no clipboard (http, an old browser): copy by hand
        }
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
    };
    return (
        <Button size="small" startIcon={<LinkIcon />} onClick={copy} title={url}>
            {copied ? 'Másolva' : label}
        </Button>
    );
};

/**
 * The room the conversation designs for: its name, its revisions to step through, and the links to
 * the room and the conversation.
 */
const RoomBar: React.FC = () => {
    const ai = React.useContext(AiDesignerContext);
    const [editing, setEditing] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);
    if (!ai.room) return null;
    const revisions = [...ai.revisions].sort((a, b) => a.n - b.n);
    const shown = ai.current?.revision ?? null;
    const at = revisions.findIndex((r) => r.n === shown);
    const revision = at >= 0 ? revisions[at] : null;
    const step = (to: number) => { if (revisions[to]) ai.showRevision(revisions[to].n); };
    const save = async () => {
        setSaving(true);
        await ai.saveRevision();
        setSaving(false);
    };

    return (
        <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1, mb: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
                {editing === null ? (
                    <>
                        <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            Szoba: {ai.roomLabel || 'névtelen'}
                        </Typography>
                        <IconButton size="small" aria-label="a szoba átnevezése" onClick={() => setEditing(ai.roomLabel ?? '')}>
                            <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </>
                ) : (
                    <>
                        <TextField size="small" value={editing} autoFocus fullWidth
                            onChange={(e) => setEditing(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && editing.trim()) { ai.renameRoom(editing.trim()); setEditing(null); }
                                if (e.key === 'Escape') setEditing(null);
                            }} />
                        <Button size="small" disabled={!editing.trim()}
                            onClick={() => { ai.renameRoom(editing.trim()); setEditing(null); }}>Mentés</Button>
                    </>
                )}
            </Stack>
            {revisions.length > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                    <span title={at === 0 ? 'Ez az első változat' : 'Előző változat'}>
                        <IconButton size="small" aria-label="előző változat" disabled={at === 0}
                            onClick={() => step(at === -1 ? revisions.length - 1 : at - 1)}>
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                    </span>
                    <Typography variant="caption" sx={{ flex: 1, textAlign: 'center' }}>
                        {revision
                            ? `${revision.n}. változat / ${revisions.length} · ${REVISION_SOURCE[revision.source]}`
                              + `${revision.kind === 'final' ? '' : ' · köztes'} · ${when(revision.created_at)}`
                            : `${revisions.length} változat — a Designerben más látszik`}
                    </Typography>
                    <span title={at === -1 || at >= revisions.length - 1 ? 'Ez a legutóbbi változat' : 'Következő változat'}>
                        <IconButton size="small" aria-label="következő változat"
                            disabled={at === -1 || at >= revisions.length - 1} onClick={() => step(at + 1)}>
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Stack>
            )}
            {ai.busy && revisions.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                    Az AI közben dolgozik: a következő ellenőrzése a saját tervét tölti vissza a Designerbe.
                </Typography>
            )}
            {ai.current?.source === 'manual' && !ai.busy && (
                <Button size="small" onClick={save} disabled={saving} sx={{ mt: 0.5 }}>
                    A módosított terv mentése új változatként
                </Button>
            )}
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                <CopyLink label="A szoba linkje" url={linkTo('room', ai.room)} />
                {ai.conversation && <CopyLink label="A beszélgetés linkje" url={linkTo('conversation', ai.conversation)} />}
            </Stack>
        </Box>
    );
};

/** The shop's earlier conversations (the room's, when one is open), from this page or the AR app. */
const EarlierList: React.FC = () => {
    const ai = React.useContext(AiDesignerContext);
    const [open, setOpen] = React.useState(false);
    const [list, setList] = React.useState<IConversation[] | null>(null);
    const [rooms, setRooms] = React.useState<Record<string, IRoom>>({});
    const [failed, setFailed] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        let gone = false;
        setFailed(false);
        Promise.all([ai.listConversations(ai.room ?? undefined), ai.listRooms()]).then(([conversations, found]) => {
            if (gone) return;
            setFailed(conversations === null);
            setList(conversations ?? []);
            setRooms(Object.fromEntries((found ?? []).map((r) => [r.id, r])));
        });
        return () => { gone = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, ai.room]);

    return (
        <Box>
            <Button size="small" startIcon={<HistoryIcon />} onClick={() => setOpen(!open)}>
                {ai.room ? 'A szoba korábbi tervezései' : 'Korábbi tervezések'}
            </Button>
            <Collapse in={open} unmountOnExit>
                {failed && <Alert severity="warning" sx={{ mt: 1 }}>Az AI tervező szolgáltatás nem válaszol.</Alert>}
                {list && list.length === 0 && !failed && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Még nincs ilyen.</Typography>
                )}
                <Stack spacing={0.5} sx={{ mt: 1, maxHeight: 320, overflow: 'auto' }}>
                    {(list ?? []).map((c) => (
                        <Stack key={c.id} direction="row" alignItems="center" spacing={1}
                            sx={{ border: '1px solid #eee', borderRadius: 1, px: 1, py: 0.5 }}>
                            {c.client === 'app' && <PhoneAndroidIcon sx={{ fontSize: 16, color: 'text.secondary' }} titleAccess="az AR alkalmazásból" />}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" noWrap title={c.title}>{c.title || '(kép, szöveg nélkül)'}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                                    {c.room && rooms[c.room]?.label ? `${rooms[c.room].label} · ` : ''}
                                    {when(c.updated_at)} · {c.revisions.length} változat
                                    {c.busy ? ' · most is dolgozik' : ''}
                                    {typeof c.cost === 'number' ? ` · ~$${c.cost.toFixed(2)}` : ''}
                                </Typography>
                            </Box>
                            <Button size="small" disabled={ai.busy || c.id === ai.conversation}
                                onClick={() => { setOpen(false); ai.openConversation(c.id); }}>
                                {c.id === ai.conversation ? 'Nyitva' : 'Megnyitás'}
                            </Button>
                        </Stack>
                    ))}
                </Stack>
            </Collapse>
        </Box>
    );
};

const connectionText = (ai: React.ContextType<typeof AiDesignerContext>) => {
    switch (ai.connection) {
        case 'open': return ai.replaying > 0 ? 'Betöltés…' : 'Kapcsolódva';
        case 'connecting': return 'Kapcsolódás…';
        case 'retrying': return `Újrakapcsolódás${ai.retryIn ? ` ${ai.retryIn} s múlva` : ''}…`;
        case 'taken': return 'Máshol nyitva';
        // no conversation yet: the socket opens with the first brief; the service answered /health
        default: return !ai.conversation && ai.choices ? 'Kész' : 'Nincs kapcsolat';
    }
};

const AiDesignerTab: React.FC = () => {
    const ai = React.useContext(AiDesignerContext);
    const brief = useImagePicker();
    const followUp = useImagePicker();
    const [prompt, setPrompt] = React.useState('');
    // Off until the first design: then the next brief builds on it, unless the admin unticks it.
    const [includeCurrent, setIncludeCurrent] = React.useState(false);
    const designed = React.useRef(false);
    const [model, setModel] = React.useState('');
    const [effort, setEffort] = React.useState('');
    const [reply, setReply] = React.useState('');
    const [bigImage, setBigImage] = React.useState<string | null>(null);
    const [dragging, setDragging] = React.useState(false);
    const feedEnd = React.useRef<HTMLDivElement>(null);

    const started = ai.feed.length > 0;
    const choices = ai.choices;
    const pickedModel = model || choices?.model || '';
    const pickedEffort = effort || choices?.effort || '';

    React.useEffect(() => {
        ai.loadChoices();
    }, [ai.loadChoices]);

    React.useEffect(() => {
        if (ai.design && !designed.current) {
            designed.current = true;
            setIncludeCurrent(true);
        }
    }, [ai.design]);

    React.useEffect(() => {
        feedEnd.current?.scrollIntoView({ block: 'end' });
    }, [ai.feed]);

    const handleStart = () => {
        if (!prompt.trim() && !brief.images.length) return;
        ai.start(prompt.trim(), brief.images, includeCurrent,
            choices ? { model: pickedModel, effort: pickedEffort } : {});
        setPrompt('');
        brief.clear();
    };

    const handleReply = () => {
        if (!reply.trim() && !followUp.images.length) return;
        ai.send(reply.trim(), followUp.images);
        setReply('');
        followUp.clear();
    };

    const drop = {
        onDragOver: (e: React.DragEvent) => { e.preventDefault(); setDragging(true); },
        onDragLeave: () => setDragging(false),
        onDrop: (e: React.DragEvent) => { e.preventDefault(); setDragging(false); brief.add(e.dataTransfer.files); },
    };

    const design = ai.design;
    const notes = ai.files.filter((f) => f.path.endsWith('.md'));

    return (
        // the shop's theme draws enabled icon buttons white and body text light grey (for the dark bars):
        // here they are on white
        <Box sx={{ '& .MuiIconButton-root:not(.Mui-disabled)': { color: 'text.secondary' }, '& .MuiTypography-body2': { color: '#1a1a1a' } }}>
            {/* the connection, the new conversation / new room buttons and the room bar stay on top while the feed scrolls */}
            <Box sx={{ position: 'sticky', top: -16, zIndex: 2, bgcolor: 'background.paper', mx: -2, px: 2, pt: 2, mt: -2, pb: 0.5,
                boxShadow: '0 4px 6px -4px rgba(0,0,0,0.15)', mb: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                <Chip size="small" variant="outlined"
                    color={ai.connection === 'open' ? 'success' : ai.connection === 'retrying' || ai.connection === 'taken' ? 'warning' : 'default'}
                    label={connectionText(ai)} />
                {ai.connection === 'taken' && (
                    <Button size="small" onClick={ai.takeBack}>Folytatás itt</Button>
                )}
                {ai.model && ai.conversation && (
                    <Typography variant="caption" color="text.secondary">
                        {ai.model}{ai.effort ? ` · ${ai.effort}` : ''}
                    </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                {ai.usage && (
                    <Typography variant="caption" color="text.secondary"
                        title={`be: ${ai.usage.input} · cache: ${ai.usage.cache_read} olvasás / ${ai.usage.cache_write} írás · ki: ${ai.usage.output} token`}>
                        {ai.usage.cost !== null ? `~$${ai.usage.cost.toFixed(2)}` : `${ai.usage.output} token`}
                    </Typography>
                )}
                {started && !ai.busy && (
                    <Button size="small" onClick={ai.reset} title="Új beszélgetés ugyanebben a szobában, a legutóbbi változatából">
                        Új beszélgetés
                    </Button>
                )}
                {(started || ai.room) && !ai.busy && (
                    <Button size="small" onClick={() => { setIncludeCurrent(false); ai.newRoom(); }}
                        title="Új szoba: új tervezési projekt, tiszta lappal">
                        Új szoba
                    </Button>
                )}
            </Stack>

            <RoomBar />
            </Box>

            {!started && (
                <Stack spacing={1.5}>
                    <Typography variant="caption" color="text.secondary">
                        Írja le, mit tervezzen az AI (szoba, konyha vagy egy bútor), és adjon hozzá képet vagy PDF-et,
                        ha van: a helyiség fotóját, rajzát vagy alaprajzát, illetve egy hangulatképet a stílushoz. Az AI a Designerben
                        építi és ellenőrzi a tervet; a végén az utoljára ellenőrzött terv marad betöltve. A tervezés
                        alatt ne módosítsa a paramétereket, mert az felülírja a Designer tartalmát.
                    </Typography>

                    <Box {...drop} onClick={brief.open}
                        sx={{ border: '1px dashed', borderColor: dragging ? 'primary.main' : '#bbb', borderRadius: 1,
                            p: 1.5, textAlign: 'center', cursor: 'pointer', bgcolor: dragging ? 'rgba(49,76,61,0.06)' : 'transparent' }}>
                        <AddPhotoAlternateIcon sx={{ color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Kép vagy PDF feltöltése: kattintson, húzza ide, vagy illessze be a szövegmezőbe (legfeljebb {MAX_IMAGES})
                        </Typography>
                        {brief.picker}
                    </Box>
                    {brief.images.length > 0 && (
                        <Thumbs images={brief.images.map((i) => i.preview)} onRemove={brief.remove} />
                    )}
                    {brief.error && <Alert severity="warning">{brief.error}</Alert>}
                    <ScanRoom onLoaded={() => setIncludeCurrent(true)} />

                    <TextField
                        label="Feladat"
                        placeholder="pl. L alakú konyha fehér frontokkal, a mosogató az ablak alatt, a hűtő a bejárat mellett"
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        onPaste={brief.onPaste}
                        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleStart(); }}
                        multiline minRows={4} maxRows={12} fullWidth
                    />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={includeCurrent} onChange={(e) => setIncludeCurrent(e.target.checked)} />}
                        label={<Typography variant="caption">A látható tervből indulj ki</Typography>}
                    />
                    {choices && (
                        <Stack direction="row" spacing={1}>
                            <TextField select size="small" label="Modell" value={pickedModel}
                                onChange={(e) => setModel(e.target.value)} sx={{ flex: 2 }}
                                helperText="USD / millió token (be / ki)">
                                {choices.models.map((m) => <MenuItem key={m.id} value={m.id}>{modelText(m)}</MenuItem>)}
                            </TextField>
                            <TextField select size="small" label="Alaposság (effort)" value={pickedEffort}
                                onChange={(e) => setEffort(e.target.value)} sx={{ flex: 1 }}>
                                {choices.efforts.map((x) => <MenuItem key={x} value={x}>{EFFORT_TEXT[x] ?? x}</MenuItem>)}
                            </TextField>
                        </Stack>
                    )}
                    <Box>
                        <Button variant="contained" size="small" onClick={handleStart}
                            disabled={!prompt.trim() && !brief.images.length}>
                            Tervezés indítása
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>Ctrl+Enter</Typography>
                    </Box>
                    <EarlierList />
                </Stack>
            )}

            {started && (
                <Stack spacing={1.25}>
                    {ai.feed.map((item) => {
                        switch (item.kind) {
                            case 'user':
                                return (
                                    <Box key={item.key} sx={{ bgcolor: 'rgba(49,76,61,0.08)', borderRadius: 1, p: 1.25 }}>
                                        {item.images.length > 0 && <Box sx={{ mb: item.text ? 1 : 0 }}>
                                            <Thumbs images={item.images} onOpen={setBigImage} />
                                        </Box>}
                                        {item.text && <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{item.text}</Typography>}
                                    </Box>
                                );
                            case 'assistant':
                                return <Markdown key={item.key} text={item.text} />;
                            case 'thinking':
                                return <ThinkingRow key={item.key} text={item.text} />;
                            case 'tool':
                                return <ToolRow key={item.key} item={item} onOpenImage={setBigImage} />;
                            case 'notice':
                                return (
                                    <Alert key={item.key} severity={item.level === 'error' ? 'error' : 'info'} sx={{ py: 0 }}>
                                        {item.text}
                                    </Alert>
                                );
                        }
                    })}

                    {ai.busy && (
                        <Box>
                            <LinearProgress />
                            <Stack direction="row" alignItems="center" sx={{ mt: 0.75 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                                    {activityText(ai.activity)}
                                </Typography>
                                <Button size="small" color="error" onClick={ai.stop}>Leállítás</Button>
                            </Stack>
                        </Box>
                    )}

                    {design && !ai.busy && (
                        <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1.25 }}>
                            <Typography variant="subtitle2">A Designerben lévő terv</Typography>
                            <Typography variant="caption" color={design.ok ? 'text.secondary' : 'warning.main'} sx={{ display: 'block', mb: 1 }}>
                                {design.ucon.path} + {design.style.path}
                                {design.ok ? ' — hibamentesen épült' : ' — az utolsó ellenőrzés hibát jelzett'}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                                <Button size="small" variant="contained" onClick={ai.applyDesign}
                                    disabled={!design.ucon.text || !design.style.text}>
                                    Újratöltés a Designerbe
                                </Button>
                                {design.ucon.text && (
                                    <Button size="small" variant="outlined" onClick={() => download(design.ucon.path, design.ucon.text!)}>
                                        UCon letöltése
                                    </Button>
                                )}
                                {design.style.text && (
                                    <Button size="small" variant="outlined" onClick={() => download(design.style.path, design.style.text!)}>
                                        Stílus letöltése
                                    </Button>
                                )}
                                {notes.map((f) => (
                                    <Button key={f.path} size="small" onClick={() => download(f.path, f.text ?? '')}>
                                        {f.path.split('/').pop()}
                                    </Button>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {!ai.busy && (
                        <Stack spacing={1}>
                            {followUp.images.length > 0 && (
                                <Thumbs images={followUp.images.map((i) => i.preview)} onRemove={followUp.remove} />
                            )}
                            <Stack direction="row" spacing={1} alignItems="flex-end">
                                <TextField
                                    placeholder="Válasz vagy módosítás, pl. legyenek a frontok zöldek"
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                    onPaste={followUp.onPaste}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleReply(); }}
                                    multiline minRows={1} maxRows={6} fullWidth size="small"
                                />
                                <IconButton aria-label="kép vagy PDF hozzáadása" onClick={followUp.open}>
                                    <AddPhotoAlternateIcon />
                                </IconButton>
                                {followUp.picker}
                                <Button variant="contained" size="small" onClick={handleReply}
                                    disabled={!reply.trim() && !followUp.images.length}>
                                    Küldés
                                </Button>
                            </Stack>
                            {followUp.error && <Alert severity="warning">{followUp.error}</Alert>}
                            <EarlierList />
                        </Stack>
                    )}
                    <div ref={feedEnd} />
                </Stack>
            )}

            <Dialog open={!!bigImage} onClose={() => setBigImage(null)} maxWidth="lg">
                {bigImage && <Box component="img" src={bigImage} alt="" onClick={() => setBigImage(null)}
                    sx={{ display: 'block', maxWidth: '90vw', maxHeight: '90vh' }} />}
            </Dialog>
        </Box>
    );
};

export default AiDesignerTab;
